import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorHandler } from '@/lib/error-handler';
import type { ReactNode } from 'react';

// eslint-disable-next-line react-refresh/only-export-components
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 10, // 10 minutos (anteriormente cacheTime)
      retry: (failureCount, error: unknown) => {
        // Transform error to check if it's recoverable
        const appError = ErrorHandler.transformError(error);

        // Don't retry on user errors or non-recoverable errors
        if (appError.isUserError || !appError.isRecoverable) {
          return false;
        }

        // Retry recoverable errors up to 3 times
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        // Transform error to check if it's recoverable
        const appError = ErrorHandler.transformError(error);

        // Don't retry on user errors or non-recoverable errors
        if (appError.isUserError || !appError.isRecoverable) {
          return false;
        }

        // Retry recoverable errors only once for mutations
        return failureCount < 1;
      },
      onError: (error: unknown) => {
        // Handle mutation errors globally
        const appError = ErrorHandler.transformError(error);
        ErrorHandler.showErrorToast(appError);
      },
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
